import { useState } from "react";
import { useParams } from "react-router-dom";
import { doc, setDoc, deleteDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../firebase";
import { useOptions } from "../hooks/useOptions";

const renderWithBold = (text) => {
    if (!text) return null;
    const parts = text.split(/\*\*(.*?)\*\*/g);
    return parts.map((part, index) => 
        index % 2 === 1 ? <strong key={index}>{part}</strong> : part
    );
};

const resizeImage = (file, maxWidth, maxHeight) => {
    return new Promise((resolve) => {
        if (!file.type.match(/image\/(jpeg|png|webp)/)) {
            resolve(file);
            return;
        }
        const reader = new FileReader();
        reader.onload = (e) => {
            const img = new Image();
            img.onload = () => {
                let width = img.width;
                let height = img.height;

                if (width > maxWidth || height > maxHeight) {
                    const ratio = Math.min(maxWidth / width, maxHeight / height);
                    width = width * ratio;
                    height = height * ratio;
                } else {
                    resolve(file); // No resize needed
                    return;
                }

                const canvas = document.createElement('canvas');
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, width, height);

                canvas.toBlob((blob) => {
                    resolve(new File([blob], file.name, { type: file.type || 'image/jpeg' }));
                }, file.type || 'image/jpeg', 0.85);
            };
            img.src = e.target.result;
        };
        reader.readAsDataURL(file);
    });
};

export default function OptionsEditor() {
    const { formId } = useParams();
    const { options, loading } = useOptions(formId);
    const [isEditing, setIsEditing] = useState(false);
    const [editOptions, setEditOptions] = useState(null);
    const [saving, setSaving] = useState(false);
    const [saveMsg, setSaveMsg] = useState(null);
    const [deletedIds, setDeletedIds] = useState([]);

    const displayOptions = isEditing ? editOptions : options;

    const startEditing = () => {
        setEditOptions(options.map((o) => ({ 
            ...o, 
            imageUrls: o.imageUrls || (o.imageUrl ? [o.imageUrl] : []),
            imageFiles: [] 
        })));
        setDeletedIds([]);
        setSaveMsg(null);
        setIsEditing(true);
    };

    const cancelEditing = () => {
        setEditOptions(null);
        setDeletedIds([]);
        setSaveMsg(null);
        setIsEditing(false);
    };

    const updateOption = (index, key, value) => {
        const updated = [...editOptions];
        if (key === "totalSpots" || key === "spotsLeft") {
            updated[index] = { ...updated[index], [key]: parseInt(value, 10) || 0 };
        } else {
            updated[index] = { ...updated[index], [key]: value };
        }
        setEditOptions(updated);
    };

    const addOption = () => {
        const newId = `option_${Date.now()}`;
        setEditOptions([
            ...editOptions,
            { id: newId, name: "", description: "", imageUrls: [], imageFiles: [], totalSpots: 20, spotsLeft: 20, isNew: true },
        ]);
    };

    const removeOption = (index) => {
        const opt = editOptions[index];
        if (!opt.isNew && opt.id) {
            setDeletedIds((prev) => [...prev, opt.id]);
        }
        setEditOptions(editOptions.filter((_, i) => i !== index));
    };

    const handleSave = async () => {
        // Validate
        for (const opt of editOptions) {
            if (!opt.name.trim()) {
                setSaveMsg({ type: "error", text: "Każda opcja musi mieć nazwę." });
                return;
            }
            if (!opt.id.trim()) {
                setSaveMsg({ type: "error", text: "Każda opcja musi mieć ID." });
                return;
            }
            if (opt.totalSpots < 0 || opt.spotsLeft < 0) {
                setSaveMsg({ type: "error", text: "Liczba miejsc nie może być ujemna." });
                return;
            }
        }

        const ids = editOptions.map((o) => o.id);
        if (new Set(ids).size !== ids.length) {
            setSaveMsg({ type: "error", text: "ID opcji muszą być unikalne." });
            return;
        }

        setSaving(true);
        setSaveMsg(null);

        try {
            // Delete removed options
            for (const id of deletedIds) {
                await deleteDoc(doc(db, "forms", formId, "options", id));
            }

            // Save all current options
            for (const opt of editOptions) {
                const { isNew, imageFiles, imageUrls, imageUrl, ...data } = opt;
                
                let finalImageUrls = [...(imageUrls || [])];
                
                if (imageFiles && imageFiles.length > 0) {
                    for (const file of imageFiles) {
                        const resizedFile = await resizeImage(file, 800, 800);
                        const fileRef = ref(storage, `forms/${formId}/options/${opt.id}_${Date.now()}_${resizedFile.name}`);
                        await uploadBytes(fileRef, resizedFile);
                        const url = await getDownloadURL(fileRef);
                        finalImageUrls.push(url);
                    }
                }

                await setDoc(doc(db, "forms", formId, "options", opt.id), {
                    name: data.name,
                    description: data.description || "",
                    imageUrls: finalImageUrls,
                    totalSpots: data.totalSpots,
                    spotsLeft: data.spotsLeft,
                });
            }

            setSaveMsg({ type: "success", text: "Zapisano!" });
            setIsEditing(false);
            setEditOptions(null);
            setDeletedIds([]);
        } catch (err) {
            setSaveMsg({ type: "error", text: `Błąd zapisu: ${err.message}` });
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="config-section">
                <p className="config-loading">Ładowanie opcji...</p>
            </div>
        );
    }

    return (
        <div className="config-section">
            <div className="config-header">
                <h2 className="summary-title">Opcje / Warsztaty</h2>
                {!isEditing ? (
                    <button className="btn-small btn-edit" onClick={startEditing}>
                        ✏️ Edytuj
                    </button>
                ) : (
                    <div className="config-actions">
                        <button
                            className="btn-small btn-save"
                            onClick={handleSave}
                            disabled={saving}
                        >
                            {saving ? "Zapisywanie..." : "💾 Zapisz"}
                        </button>
                        <button className="btn-small btn-cancel" onClick={cancelEditing}>
                            Anuluj
                        </button>
                    </div>
                )}
            </div>

            {saveMsg && (
                <div
                    className={`config-msg ${saveMsg.type === "error" ? "config-msg-error" : "config-msg-success"
                        }`}
                >
                    {saveMsg.text}
                </div>
            )}

            <div className="config-fields-list">
                <p className="config-fields-label">
                    Opcje ({displayOptions.length})
                </p>

                {displayOptions.map((opt, index) => (
                    <div key={opt.id + index} className="config-field-row">
                        {isEditing ? (
                            <>
                                <div style={{ display: 'flex', flexDirection: 'column', flex: 1, gap: '0.75rem' }}>
                                    <div className="config-field-inputs">
                                    <div className="config-input-group">
                                        <label>ID</label>
                                        <input
                                            type="text"
                                            value={opt.id}
                                            onChange={(e) => updateOption(index, "id", e.target.value)}
                                            className="input-sm"
                                            disabled={!opt.isNew}
                                        />
                                    </div>
                                    <div className="config-input-group">
                                        <label>Nazwa (podgląd z formularza)</label>
                                        <textarea
                                            value={opt.name}
                                            onChange={(e) =>
                                                updateOption(index, "name", e.target.value)
                                            }
                                            style={{
                                                width: "100%",
                                                maxWidth: "330px",
                                                resize: "vertical",
                                                fontFamily: "inherit",
                                                fontSize: "0.9rem",
                                                fontWeight: "500",
                                                color: "#2d2a26",
                                                lineHeight: "1.4",
                                                padding: "0.5rem",
                                                margin: "0",
                                                border: "1.5px solid #e5e0db",
                                                borderRadius: "8px",
                                                whiteSpace: "pre-wrap",
                                                minHeight: "45px"
                                            }}
                                            placeholder="Nazwa opcji/warsztatu"
                                        />
                                    </div>
                                    <div className="config-input-group config-input-narrow">
                                        <label>Wszystkie</label>
                                        <input
                                            type="number"
                                            value={opt.totalSpots}
                                            onChange={(e) =>
                                                updateOption(index, "totalSpots", e.target.value)
                                            }
                                            className="input-sm"
                                            min="0"
                                        />
                                    </div>
                                    <div className="config-input-group config-input-narrow">
                                        <label>Wolne</label>
                                        <input
                                            type="number"
                                            value={opt.spotsLeft}
                                            onChange={(e) =>
                                                updateOption(index, "spotsLeft", e.target.value)
                                            }
                                            className="input-sm"
                                            min="0"
                                        />
                                    </div>
                                    </div>
                                    <div className="config-input-group" style={{ flex: '1 1 100%' }}>
                                        <label>Obrazki (możesz wgrać wiele plików)</label>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                            {(opt.imageUrls || []).map((url, urlIndex) => (
                                                <div key={urlIndex} style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                                                    <img src={url} alt="Podgląd" style={{ maxHeight: "40px", borderRadius: "4px", objectFit: "cover" }} />
                                                    <input 
                                                        type="text" 
                                                        value={url}
                                                        onChange={(e) => {
                                                            const newUrls = [...opt.imageUrls];
                                                            newUrls[urlIndex] = e.target.value;
                                                            updateOption(index, "imageUrls", newUrls);
                                                        }}
                                                        className="input-sm"
                                                        style={{ flex: 1, maxWidth: "250px", fontSize: "0.85rem" }}
                                                    />
                                                    <button type="button" onClick={() => {
                                                        const newUrls = opt.imageUrls.filter((_, i) => i !== urlIndex);
                                                        updateOption(index, "imageUrls", newUrls);
                                                    }} style={{ background: "none", border: "none", color: "red", cursor: "pointer", fontSize: "1rem" }}>✕</button>
                                                </div>
                                            ))}
                                            
                                            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                                                <input
                                                    type="text"
                                                    disabled
                                                    placeholder="Wklej URL lub wybierz pliki"
                                                    className="input-sm"
                                                    style={{ flex: 1, maxWidth: "250px", fontSize: "0.85rem" }}
                                                />
                                                <button type="button" onClick={() => updateOption(index, "imageUrls", [...(opt.imageUrls || []), ""])} style={{ padding: "0.2rem 0.5rem", borderRadius: "4px", border: "1px solid #ccc", cursor: "pointer" }}>+ URL</button>
                                            </div>

                                            {(opt.imageFiles || []).map((file, fileIndex) => (
                                                <div key={`file_${fileIndex}`} style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                                                    <span style={{ fontSize: "0.85rem", color: "#4a7c3f", fontWeight: "600" }}>✔ Nowy plik: {file.name}</span>
                                                    <button type="button" onClick={() => {
                                                        const newFiles = opt.imageFiles.filter((_, i) => i !== fileIndex);
                                                        updateOption(index, "imageFiles", newFiles);
                                                    }} style={{ background: "none", border: "none", color: "red", cursor: "pointer", fontSize: "1.rem", padding: 0 }}>✕</button>
                                                </div>
                                            ))}

                                            <input
                                                type="file"
                                                accept="image/*"
                                                multiple
                                                onChange={(e) => {
                                                    if (e.target.files && e.target.files.length > 0) {
                                                        const newFiles = Array.from(e.target.files);
                                                        updateOption(index, "imageFiles", [...(opt.imageFiles || []), ...newFiles]);
                                                    }
                                                    e.target.value = ""; // reset input
                                                }}
                                                className="input-sm"
                                                style={{ width: "100%", maxWidth: "330px" }}
                                            />
                                        </div>
                                    </div>
                                    <div className="config-input-group" style={{ flex: '1 1 100%' }}>
                                        <label>Opis warsztatu (podgląd z formularza)</label>
                                        <textarea
                                            value={opt.description || ""}
                                            onChange={(e) => updateOption(index, "description", e.target.value)}
                                            rows="3"
                                            style={{
                                                width: "100%",
                                                maxWidth: "330px",
                                                resize: "vertical",
                                                fontFamily: "inherit",
                                                fontSize: "0.9rem",
                                                color: "#8a8580",
                                                lineHeight: "1.4",
                                                padding: "0.5rem",
                                                margin: "0",
                                                border: "1.5px solid #e5e0db",
                                                borderRadius: "8px",
                                                whiteSpace: "pre-wrap"
                                            }}
                                            placeholder="Dodaj przydatne informacje o tym warsztacie... (użyj **tekst** aby pogrubić)"
                                        />
                                    </div>
                                </div>
                                <div className="config-field-actions">
                                    <button
                                        className="btn-icon btn-icon-danger"
                                        onClick={() => removeOption(index)}
                                        title="Usuń"
                                    >
                                        ✕
                                    </button>
                                </div>
                            </>
                        ) : (
                            <div className="config-field-display">
                                {((opt.imageUrls && opt.imageUrls.length > 0) ? opt.imageUrls : (opt.imageUrl ? [opt.imageUrl] : [])).length > 0 && (
                                    <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", marginBottom: "0.5rem" }}>
                                        {((opt.imageUrls && opt.imageUrls.length > 0) ? opt.imageUrls : (opt.imageUrl ? [opt.imageUrl] : [])).map((url, i) => (
                                            <img key={i} src={url} alt={opt.name} style={{ height: "40px", borderRadius: "4px", objectFit: "cover", display: "block" }} />
                                        ))}
                                    </div>
                                )}
                                {opt.imageFiles && opt.imageFiles.length > 0 && (
                                    <span style={{ fontSize: "0.85rem", color: "#4a7c3f", marginBottom: "0.5rem", display: "block", fontWeight: "600" }}>✔ Nowe pliki do wgrania: {opt.imageFiles.length}</span>
                                )}
                                <span className="field-label" style={{ whiteSpace: "pre-wrap", lineHeight: "1.4" }}>{opt.name}</span>
                                {opt.description && (
                                    <p style={{ margin: "0.25rem 0 0.5rem", fontSize: "0.9rem", color: "#8a8580", whiteSpace: "pre-wrap" }}>
                                        {renderWithBold(opt.description)}
                                    </p>
                                )}
                                <span className="field-meta">
                                    {opt.id} · {opt.totalSpots - opt.spotsLeft}/{opt.totalSpots}{" "}
                                    zajętych · {opt.spotsLeft} wolnych
                                </span>
                            </div>
                        )}
                    </div>
                ))}

                {isEditing && (
                    <button className="btn-small btn-add-field" onClick={addOption}>
                        + Dodaj opcję
                    </button>
                )}
            </div>
        </div>
    );
}
